// pages/Certificaciones.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/Certificaciones.css';
import { useFiscal } from '../hooks/useFiscal';

const Certificaciones = () => {
  const fiscal = useFiscal();
  const { proyectoId } = useParams();
  const navigate = useNavigate();
  
  const [certificaciones, setCertificaciones] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [proyecto, setProyecto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todos');

  useEffect(() => {
    cargarDatos();
  }, [proyectoId]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [resCert, resProyecto] = await Promise.all([
        api.get(`/proyectos/${proyectoId}/certificaciones`),
        api.get(`/proyectos/${proyectoId}`)
      ]);
      
      setCertificaciones(resCert.data.certificaciones);
      setResumen(resCert.data.resumen);
      setProyecto(resProyecto.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
      alert('Error al cargar certificaciones');
    } finally {
      setLoading(false);
    }
  };

  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: fiscal.moneda?.codigo || 'DOP'
    }).format(valor || 0);
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(String(fecha).substring(0,10) + 'T00:00:00').toLocaleDateString('es-DO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getBadgeClass = (estado) => {
    const classes = {
      'borrador': 'badge-borrador',
      'revisión': 'badge-revision',
      'aprobada': 'badge-aprobada',
      'facturada': 'badge-facturada'
    };
    return classes[estado] || 'badge-borrador';
  };

  const getBadgeIcon = (estado) => {
    const icons = {
      'borrador': '📝',
      'revisión': '🔍',
      'aprobada': '✅',
      'facturada': '💰'
    };
    return icons[estado] || '📝';
  };

  const handleNuevaCertificacion = () => {
    navigate(`/proyectos/${proyectoId}/certificaciones/nueva`);
  };

  const handleVerDetalle = (certId) => {
    navigate(`/certificaciones/${certId}`);
  };

  const handleEliminar = async (certId, numeroCert) => {
    if (!window.confirm(`¿Eliminar la certificación #${numeroCert}?`)) {
      return;
    }

    try {
      await api.delete(`/certificaciones/${certId}`);
      alert('Certificación eliminada exitosamente');
      cargarDatos();
    } catch (error) {
      console.error('Error eliminando certificación:', error);
      alert(error.response?.data?.error || 'Error al eliminar certificación');
    }
  };

  const certificacionesFiltradas = filtroEstado === 'todos'
    ? certificaciones
    : certificaciones.filter(c => c.estado === filtroEstado);

  const calcularPorcentajeCertificado = () => {
    if (!proyecto?.valor_contrato || proyecto.valor_contrato === 0) return 0;
    return ((resumen?.monto_total_certificado || 0) / proyecto.valor_contrato * 100).toFixed(2);
  };

  if (loading) {
    return (
      <div className="certificaciones-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Cargando certificaciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="certificaciones-container">
      {/* Header */}
      <div className="cert-header">
        <div className="header-info">
          <button 
            onClick={() => navigate(`/proyectos/${proyectoId}`)}
            className="btn-back"
          >
            ← Volver al Proyecto
          </button>
          <h1>Certificaciones de Obra</h1>
          <p className="proyecto-nombre">{proyecto?.nombre}</p>
        </div>
        <button 
          onClick={handleNuevaCertificacion}
          className="btn-primary"
        >
          ➕ Nueva Certificación
        </button>
      </div>

      {/* Resumen Cards */}
      {resumen && (
        <div className="resumen-grid">
          <div className="resumen-card valor-contrato">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <span className="card-label">Valor del Contrato</span>
              <span className="card-value">{formatearMoneda(proyecto?.valor_contrato)}</span>
            </div>
          </div>

          <div className="resumen-card monto-certificado">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <span className="card-label">Total Certificado</span>
              <span className="card-value">{formatearMoneda(resumen.monto_total_certificado)}</span>
              <span className="card-percentage">{calcularPorcentajeCertificado()}% del contrato</span>
            </div>
          </div>

          <div className="resumen-card saldo-pendiente">
            <div className="card-icon">💵</div>
            <div className="card-content">
              <span className="card-label">Saldo Pendiente</span>
              <span className="card-value">
                {formatearMoneda((proyecto?.valor_contrato || 0) - (resumen.monto_total_certificado || 0))}
              </span>
            </div>
          </div>

          <div className="resumen-card total-certs">
            <div className="card-icon">📋</div>
            <div className="card-content">
              <span className="card-label">Total Certificaciones</span>
              <span className="card-value-number">{resumen.total}</span>
              <div className="estados-mini">
                <span className="estado-count">📝 {resumen.borradores}</span>
                <span className="estado-count">🔍 {resumen.revision}</span>
                <span className="estado-count">✅ {resumen.aprobadas}</span>
                <span className="estado-count">💰 {resumen.facturadas}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="filtros-container">
        <div className="filtros">
          <button
            className={`filtro-btn ${filtroEstado === 'todos' ? 'active' : ''}`}
            onClick={() => setFiltroEstado('todos')}
          >
            Todas ({certificaciones.length})
          </button>
          <button
            className={`filtro-btn ${filtroEstado === 'borrador' ? 'active' : ''}`}
            onClick={() => setFiltroEstado('borrador')}
          >
            📝 Borradores ({resumen?.borradores || 0})
          </button>
          <button
            className={`filtro-btn ${filtroEstado === 'revisión' ? 'active' : ''}`}
            onClick={() => setFiltroEstado('revisión')}
          >
            🔍 En Revisión ({resumen?.revision || 0})
          </button>
          <button
            className={`filtro-btn ${filtroEstado === 'aprobada' ? 'active' : ''}`}
            onClick={() => setFiltroEstado('aprobada')}
          >
            ✅ Aprobadas ({resumen?.aprobadas || 0})
          </button>
          <button
            className={`filtro-btn ${filtroEstado === 'facturada' ? 'active' : ''}`}
            onClick={() => setFiltroEstado('facturada')}
          >
            💰 Facturadas ({resumen?.facturadas || 0})
          </button>
        </div>
      </div>

      {/* Lista de Certificaciones */}
      <div className="certificaciones-list">
        {certificacionesFiltradas.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No hay certificaciones</h3>
            <p>Comienza creando tu primera certificación de obra</p>
            <button onClick={handleNuevaCertificacion} className="btn-primary">
              ➕ Crear Primera Certificación
            </button>
          </div>
        ) : (
          <div className="cert-grid">
            {certificacionesFiltradas.map(cert => (
              <div key={cert.id} className="cert-card">
                <div className="cert-card-header">
                  <div className="cert-numero">
                    <span className="numero-label">Certificación</span>
                    <span className="numero-value">#{cert.numero_certificacion}</span>
                  </div>
                  <span className={`badge ${getBadgeClass(cert.estado)}`}>
                    {getBadgeIcon(cert.estado)} {cert.estado}
                  </span>
                </div>

                <div className="cert-card-body">
                  <div className="cert-info-row">
                    <span className="info-label">📅 Fecha:</span>
                    <span className="info-value">{formatearFecha(cert.fecha)}</span>
                  </div>

                  {cert.periodo_inicio && cert.periodo_fin && (
                    <div className="cert-info-row">
                      <span className="info-label">📆 Período:</span>
                      <span className="info-value">
                        {formatearFecha(cert.periodo_inicio)} - {formatearFecha(cert.periodo_fin)}
                      </span>
                    </div>
                  )}

                  <div className="cert-info-row">
                    <span className="info-label">📦 Partidas:</span>
                    <span className="info-value">{cert.partidas?.length || 0}</span>
                  </div>

                  <div className="cert-monto">
                    <span className="monto-label">Monto Certificado</span>
                    <span className="monto-value">{formatearMoneda(cert.monto_total)}</span>
                  </div>

                  {cert.factura && (
                    <div className="cert-factura">
                      💰 Factura: {cert.factura.numero_factura} - {cert.factura.ncf}
                    </div>
                  )}

                  {cert.observaciones && (
                    <div className="cert-observaciones">
                      📝 {cert.observaciones}
                    </div>
                  )}
                </div>

                <div className="cert-card-footer">
                <button
                 onClick={(e) => {
                    e.stopPropagation();
                     handleVerDetalle(cert.id);
                    }}
                       className="btn-secondary"
                         >
                         👁️ Ver Detalle
                         </button>
                  
                  {cert.estado === 'borrador' && (
                    <button
                      onClick={() => handleEliminar(cert.id, cert.numero_certificacion)}
                      className="btn-danger"
                    >
                      🗑️ Eliminar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Certificaciones;